// Domain 07 check helpers — read live (cloned) state; never mutate the lab.
import { clone } from '../../engine/net/model.js';
import { roundTrip } from '../../engine/net/sim.js';
import { ntpState, monitoringView } from '../../engine/net/services.js';
import { unsaved, configText } from '../../engine/ios/mgmtCli.js';
import { flashFile, bootProblem, flashFree, imageMd5 } from '../../engine/net/system.js';
import { pwMatches } from '../../engine/net/security.js';
import { CRED, MON, NTP, TFTP, IMG, ROUTERS, HOSTS } from './topologies.js';
export { ios, norm, yes, no, pinged } from '../d05/helpers.js';

export const D = (rt, id) => rt.net.devices[id];
export const S = (rt, id) => rt.net.devices[id].sys;
export const ping = (rt, src, dst) => roundTrip(clone(rt.net), src, dst).ok;
export const udp = (rt, src, dst, port) => roundTrip(clone(rt.net), src, dst, { proto: 'udp', port }).ok;
export const isSaved = (rt, id) => !!D(rt, id).startup && !unsaved(rt.net, id);
export const runText = (rt, id) => configText(rt.net, id).join('\n');
export const startText = (rt, id) => (D(rt, id).startup || []).join('\n');
export const has = (rt, id, re) => re.test(runText(rt, id));
export const ntp = (rt, id) => ntpState(clone(rt.net))[id] || { synced: false, stratum: 16 };
export const nms = (rt) => monitoringView(clone(rt.net), MON.ip, MON.community);
export const tftpFile = (rt, name) => D(rt, 'SRV-TFTP').tftp.files[name] || null;
export const runningImage = (rt, id) => S(rt, id).image;
export const enableOk = (rt, id) => pwMatches(D(rt, id).mgmt?.enableSecret, CRED.enable);
/** The whole site works end to end (NOC ↔ branch ↔ servers ↔ ISP edge). */
export const siteUp = (rt) => ping(rt, 'PC-OPS', HOSTS['PC-BR']) && ping(rt, 'PC-BR', TFTP.ip) && ping(rt, 'PC-OPS', '10.70.20.2') && ping(rt, 'PC-OPS', '10.70.10.2') && ping(rt, 'R1', '203.0.113.33');
/** A TFTP backup of a device that matches its current running configuration. */
export const backupCurrent = (rt, id, name) => { const f = tftpFile(rt, name); return !!f && f.kind === 'config' && f.lines.filter((l) => l !== '').join('\n') === configText(rt.net, id).filter((l) => l !== '').join('\n'); };
/** Monitoring as built for a router: NTP synced to SRV-NTP, syslog + SNMP to SRV-MON, timestamps. */
export function monOk(rt, id) {
  const d = D(rt, id);
  const t = ntp(rt, id);
  const v = nms(rt);
  return (d.ntp?.servers || []).includes(NTP) && t.synced && t.stratum === 2 && !!d.timestamps
    && (d.logging?.hosts || []).includes(MON.ip) && v.polled[d.hostname]?.ok && !!d.snmp?.traps && (d.snmp?.hosts || []).some((h) => h.ip === MON.ip && h.community === MON.community);
}
export const allMonOk = (rt) => ROUTERS.every((id) => monOk(rt, id));
export { CRED, MON, NTP, TFTP, IMG, ROUTERS, HOSTS, flashFile, bootProblem, flashFree, imageMd5, unsaved };
export { md5Of } from '../../engine/net/system.js';

// ------------------------------------------------------------ evidence
/** A copy … tftp/flash/startup command that completed (key + no error). */
export const copied = (device, keys, re) => (ev) => ev.type === 'cmd' && ev.device === device && [].concat(keys).includes(ev.key) && ev.ok !== false && (!re || re.test(ev.output || ''));
/** The device finished a reload (boot output seen). */
export const reloaded = (device) => (ev) => ev.type === 'cmd' && ev.device === device && ['reload', 'reset', 'boot', 'boot <FLASHFILE>'].includes(ev.key) && /Self decompressing the image/.test(ev.output || '');
export const TO_TFTP = ['copy running-config tftp:', 'copy running-config tftp'];
export const FROM_TFTP_RUN = ['copy tftp: running-config', 'copy tftp running-config'];
export const FROM_TFTP_START = ['copy tftp: startup-config', 'copy tftp startup-config'];
export const TO_FLASH = ['copy tftp: flash:', 'copy tftp flash', 'copy tftp: flash0:'];
export const SAVE = ['copy running-config startup-config', 'write memory', 'write'];
export const winRan = (device, key, re) => (ev) => ev.type === 'cmd' && ev.device === device && ev.key === key && ev.ok !== false && (!re || re.test(ev.output || ''));

// ------------------------------------------------------------ design comparison
/** Configuration lines with their block as context ("interface G0/1>> ip address …"). */
export function ctxLines(lines) {
  let blk = '';
  return lines.flatMap((l) => String(l).split('\n')).filter((l) => l && l.trim() !== '!' && !/^(Building configuration|Current configuration|end$|version |Using \d+)/.test(l)).map((l) => {
    if (!l.startsWith(' ')) { blk = l; return l; }
    return `${blk}>>${l}`;
  });
}
/** Lines of `want` that are missing from the device's running-config (extras are allowed). */
export const missingLines = (rt, id, want) => { const h = new Set(ctxLines(configText(rt.net, id))); return ctxLines(want).filter((l) => !h.has(l)); };
/** Reference configuration text of a device in a freshly built reference network (memoised per builder). */
const refMemo = new Map();
export function refText(build, id) {
  if (!refMemo.has(build)) refMemo.set(build, build());
  return configText(refMemo.get(build), id);
}
export const matchesDesign = (rt, id, build) => missingLines(rt, id, refText(build, id)).length === 0;

/** Replace a device with a factory-new unit of the same model (no config, nothing saved); its console starts over. */
export function factoryNew(rt, id) {
  const sh = rt.shell(id);
  sh.rebuild(null);
  rt.net.devices[id].startup = null;
  sh.resetState();
}
/** Reload a device as part of a lab's back-story (the learner's console starts fresh afterwards). */
export function rebootNow(rt, id) {
  const sh = rt.shell(id);
  sh.reloadDevice();
  sh.resetState(rt.net.devices[id].sys.rommon ? { mode: 'rommon' } : {});
}
